import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminFetchBelastingdienstForm,
  adminDownloadBelastingdienstPdf,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";

/**
 * BelastingdienstFormModal
 *
 * Toont wat er op het Belastingdienst-formulier ON0202 ("Opgaaf startende
 * onderneming") komt te staan voordat de PDF gedownload wordt. De admin kan de
 * vier velden aanpassen die een keuze zijn (handelsnaam, startdatum, vraag 8a
 * en 8b); persoonsgegevens worden bewust niet hier bewerkt maar in de aanvraag
 * zelf, zodat er één bron van waarheid blijft.
 *
 * De PDF wordt niet opgeslagen: hij wordt bij elke download opnieuw gemaakt.
 */

/** Lange optieteksten van het formulier inkorten voor in een dropdown. */
const kort = (tekst, max = 90) =>
  tekst.length > max ? `${tekst.slice(0, max).trim()}…` : tekst;

/** dd-mm-jjjj naar het formaat van een date-input. */
const naarInputDatum = (parts) =>
  parts ? `${parts.jaar}-${parts.maand}-${parts.dag}` : "";

const toonDatum = (parts) =>
  parts ? `${parts.dag}-${parts.maand}-${parts.jaar}` : "-";

/**
 * Bij `responseType: blob` komt ook een foutantwoord als blob binnen, dus de
 * JSON eromheen moet er zelf uitgepeld worden.
 */
const leesFoutBody = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch (e) {
      return null;
    }
  }
  return data ?? null;
};

const Veld = ({ label, hint, children }) => (
  <label className="block text-sm">
    <span className="font-medium text-gray-900">{label}</span>
    {children}
    {hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
  </label>
);

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50";

const BelastingdienstFormModal = ({
  isOpen,
  onClose,
  onGenerated,
  applicationId,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [waarschuwingen, setWaarschuwingen] = useState([]);
  const [overrides, setOverrides] = useState(null);
  // De vaste antwoorden staan apart: dat zijn geen tekstvelden, dus ze doen
  // niet mee aan het terugschrijven van genormaliseerde waarden hieronder.
  const [keuzeState, setKeuzeState] = useState(null);
  // Gezet als de server het formulier weigerde wegens blokkerende problemen.
  const [geblokkeerd, setGeblokkeerd] = useState(false);

  useBodyScrollLock(isOpen);

  // Elke blur vuurt een nieuw verzoek af; alleen het laatste mag de weergave
  // aanraken, anders wint een traag antwoord van een oudere waarde.
  const verzoekTeller = useRef(0);

  const laad = useCallback(
    async (verzonden, keuzen) => {
      const mijnVerzoek = verzoekTeller.current + 1;
      verzoekTeller.current = mijnVerzoek;
      setLoading(true);
      setError(null);
      try {
        const res = await adminFetchBelastingdienstForm(applicationId, {
          ...(verzonden ?? {}),
          ...(keuzen ?? {}),
        });
        if (mijnVerzoek !== verzoekTeller.current) return;
        const data = res?.data ?? res;
        const nieuweWaarschuwingen = data?.warnings ?? [];
        setPreview(data);
        setWaarschuwingen(nieuweWaarschuwingen);
        if (!nieuweWaarschuwingen.some((w) => w.level === "error")) {
          setGeblokkeerd(false);
        }

        const vanServer = {
          handelsnaam: data?.input?.handelsnaam ?? "",
          straat: data?.input?.adres?.straat ?? "",
          huisnummer: data?.input?.adres?.huisnummer ?? "",
          toevoeging: data?.input?.adres?.toevoeging ?? "",
          postcode: data?.input?.adres?.postcode ?? "",
          plaats: data?.input?.adres?.plaats ?? "",
          telefoon: data?.input?.telefoon ?? "",
          geboortedatum: naarInputDatum(data?.input?.geboortedatum),
          bsn: data?.input?.bsn ?? "",
          startdatum: naarInputDatum(data?.input?.startdatum),
          omzetStartjaar: data?.input?.omzetStartjaar ?? "",
          kostenStartjaar: data?.input?.kostenStartjaar ?? "",
        };

        if (!verzonden) {
          // Eerste keer: het voorstel van de server wordt de inhoud van de
          // formuliervelden, zodat wat je ziet ook is wat er gegenereerd wordt.
          setOverrides(vanServer);
          setKeuzeState({
            keuzes: data?.input?.keuzes ?? {},
            activiteit: data?.input?.activiteit ?? 0,
            activiteitOmschrijving: data?.input?.activiteitOmschrijving ?? "",
            aantalAfnemers: data?.input?.aantalAfnemers ?? "",
            stukken: data?.input?.stukken ?? [],
          });
          return;
        }

        // Daarna alleen terugschrijven wat de server anders invulde dan wij
        // stuurden. Zo zie je meteen dat een leeggemaakt veld terugvalt op de
        // waarde uit de aanvraag. Een veld dat sinds het verzoek opnieuw is
        // aangeraakt blijft ongemoeid, anders zou je typwerk onder je handen
        // vandaan verdwijnen.
        setOverrides((prev) => {
          const volgende = { ...(prev ?? {}) };
          Object.keys(vanServer).forEach((veld) => {
            const verstuurd = verzonden[veld] ?? "";
            const onaangeroerd = (prev?.[veld] ?? "") === verstuurd;
            if (onaangeroerd && verstuurd !== vanServer[veld]) {
              volgende[veld] = vanServer[veld];
            }
          });
          return volgende;
        });
      } catch (e) {
        if (mijnVerzoek !== verzoekTeller.current) return;
        setError(
          e?.response?.data?.error ??
            e?.message ??
            "Het voorstel kon niet geladen worden.",
        );
      } finally {
        if (mijnVerzoek === verzoekTeller.current) setLoading(false);
      }
    },
    [applicationId],
  );

  useEffect(() => {
    if (!isOpen || !applicationId) return;
    setPreview(null);
    setOverrides(null);
    setKeuzeState(null);
    setWaarschuwingen([]);
    setGeblokkeerd(false);
    laad(null, null);
  }, [isOpen, applicationId, laad]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !downloading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, downloading, onClose]);

  const zet = (veld, waarde) =>
    setOverrides((prev) => ({ ...(prev ?? {}), [veld]: waarde }));

  /** Een vast antwoord wijzigen: direct herberekenen, want het zijn keuzes. */
  const zetKeuze = (wijziging) => {
    const volgende = { ...(keuzeState ?? {}), ...wijziging };
    setKeuzeState(volgende);
    if (overrides) laad(overrides, volgende);
  };

  // Waarschuwingen opnieuw laten berekenen zodra een veld verlaten wordt. De
  // blokkade wordt pas opgeheven als het antwoord binnen is en er geen fouten
  // meer zijn, zodat de knop niet onder de cursor verandert.
  const herbereken = () => {
    if (overrides) laad(overrides, keuzeState);
  };

  const download = async (force = false) => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await adminDownloadBelastingdienstPdf(applicationId, {
        ...(overrides ?? {}),
        ...(keuzeState ?? {}),
        ...(force ? { force: true } : {}),
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = preview?.bestandsnaam ?? "belastingdienst-formulier.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setGeblokkeerd(false);
      // Het formulier is gemaakt en de download loopt, dus deze modal is klaar.
      // onGenerated ververst de aanvraag, zodat de nieuwe activiteitenregel en
      // het opgeslagen bestand meteen in beeld staan.
      if (onGenerated) onGenerated();
      onClose();
    } catch (e) {
      const body = await leesFoutBody(e);
      if (e?.response?.status === 422) {
        // De server weigert een formulier met ontbrekende of foute gegevens.
        if (body?.warnings) setWaarschuwingen(body.warnings);
        setGeblokkeerd(true);
        setError(
          body?.error ??
            "Er zijn problemen met de gegevens van deze aanvraag.",
        );
      } else {
        setError(
          body?.error ??
            e?.message ??
            "Het formulier kon niet gegenereerd worden.",
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const input = preview?.input;
  const opties = preview?.opties;
  const fouten = waarschuwingen.filter((w) => w.level === "error");
  const aandacht = waarschuwingen.filter((w) => w.level !== "error");

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Belastingdienst-formulier"
    >
      {/*
        De achtergrond is een eigen laag en niet de scrollcontainer zelf: anders
        telt een klik op de scrollbalk ook als een klik naast de modal.
      */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onMouseDown={() => {
          if (!downloading) onClose();
        }}
      />
      <div className="relative my-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div>
            <h2 className="font-display text-xl text-gray-900">
              Belastingdienst-formulier
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Opgaaf startende onderneming (ON0202). Controleer de gegevens en
              download het ingevulde formulier.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={downloading}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {loading && !preview ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Voorstel laden…
            </div>
          ) : error && !preview ? (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
              {error}
            </div>
          ) : input ? (
            <>
              {error ? (
                <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
                  {error}
                </div>
              ) : null}

              {fouten.length ? (
                <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50 p-4">
                  <p className="text-sm font-semibold text-rose-900">
                    Dit ontbreekt of klopt niet
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-rose-900">
                    {fouten.map((w, i) => (
                      <li key={`f-${i}`}>
                        <span className="font-medium">{w.veld}:</span> {w.bericht}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {aandacht.length ? (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-900">
                    Even controleren
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-900">
                    {aandacht.map((w, i) => (
                      <li key={`a-${i}`}>
                        <span className="font-medium">{w.veld}:</span> {w.bericht}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Veld label="Vraag 1a Handelsnaam">
                  <input
                    type="text"
                    value={overrides?.handelsnaam ?? ""}
                    onChange={(e) => zet("handelsnaam", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld
                  label="Vraag 1c Startdatum onderneming"
                  hint={
                    input.startdatumBron === "handmatig"
                      ? "Handmatig ingevuld"
                      : input.startdatumBron === "onbekend"
                      ? "Niet af te leiden uit de aanvraag"
                      : `Overgenomen uit de ${input.startdatumBron} (de laatste van factuur- en installatiedatum)`
                  }
                >
                  <input
                    type="date"
                    value={overrides?.startdatum ?? ""}
                    onChange={(e) => zet("startdatum", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld label="Vraag 8a Schatting omzet startjaar (€)">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={overrides?.omzetStartjaar ?? ""}
                    onChange={(e) => zet("omzetStartjaar", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld
                  label="Vraag 8b Kosten en investeringen (€)"
                  hint={
                    input.aankoopbedragOpgegeven
                      ? `Opgegeven aankoopbedrag: ${input.aankoopbedragOpgegeven}, afgerond op hele euro's`
                      : "Geen aankoopbedrag in de aanvraag"
                  }
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    value={overrides?.kostenStartjaar ?? ""}
                    onChange={(e) => zet("kostenStartjaar", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>
              </div>

              <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">
                  Gegevens van de aanvrager
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Voorgevuld uit de aanvraag. Een wijziging hier geldt alleen
                  voor dit formulier; de aanvraag zelf blijft ongewijzigd.
                </p>

                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Veld label="Straatnaam (vraag 1b en 3)">
                    <input
                      type="text"
                      value={overrides?.straat ?? ""}
                      onChange={(e) => zet("straat", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <div className="grid grid-cols-2 gap-3">
                    <Veld label="Huisnummer">
                      <input
                        type="text"
                        value={overrides?.huisnummer ?? ""}
                        onChange={(e) => zet("huisnummer", e.target.value)}
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                    <Veld label="Toevoeging">
                      <input
                        type="text"
                        value={overrides?.toevoeging ?? ""}
                        onChange={(e) => zet("toevoeging", e.target.value)}
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                  </div>

                  <Veld label="Postcode">
                    <input
                      type="text"
                      value={overrides?.postcode ?? ""}
                      onChange={(e) => zet("postcode", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Plaats">
                    <input
                      type="text"
                      value={overrides?.plaats ?? ""}
                      onChange={(e) => zet("plaats", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Telefoonnummer" hint="Tien cijfers, het vakje heeft tien hokjes">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={overrides?.telefoon ?? ""}
                      onChange={(e) => zet("telefoon", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Geboortedatum">
                    <input
                      type="date"
                      value={overrides?.geboortedatum ?? ""}
                      onChange={(e) => zet("geboortedatum", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Burgerservicenummer" hint="Negen cijfers">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={overrides?.bsn ?? ""}
                      onChange={(e) => zet("bsn", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={`${inputClass} font-mono`}
                    />
                  </Veld>
                </div>
              </div>

              <details className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-semibold text-gray-900">
                  Vaste antwoorden aanpassen
                  <span className="ml-2 font-normal text-gray-500">
                    (vraag 2, 6a, 7, 8c, 8d, 8e, 9 en 10)
                  </span>
                </summary>
                <p className="mt-2 text-xs text-gray-500">
                  Deze staan goed voor een gewone thuisbatterij-aanvraag. Wijk je
                  ervan af, dan krijg je daar een waarschuwing over.
                </p>

                <div className="mt-3 space-y-3">
                  {opties
                    ? Object.entries(opties.keuzevelden).map(
                        ([sleutel, veld]) => (
                          <Veld key={sleutel} label={veld.vraag}>
                            <select
                              value={keuzeState?.keuzes?.[sleutel] ?? 0}
                              onChange={(e) =>
                                zetKeuze({
                                  keuzes: {
                                    ...(keuzeState?.keuzes ?? {}),
                                    [sleutel]: Number(e.target.value),
                                  },
                                })
                              }
                              disabled={downloading}
                              className={inputClass}
                            >
                              {veld.opties.map((o) => (
                                <option key={o.index} value={o.index}>
                                  {kort(o.tekst)}
                                </option>
                              ))}
                            </select>
                          </Veld>
                        ),
                      )
                    : null}

                  {opties ? (
                    <Veld label="Vraag 7 Aard van de onderneming">
                      <select
                        value={keuzeState?.activiteit ?? 0}
                        onChange={(e) =>
                          zetKeuze({ activiteit: Number(e.target.value) })
                        }
                        disabled={downloading}
                        className={inputClass}
                      >
                        {opties.activiteiten.map((o) => (
                          <option key={o.index} value={o.index}>
                            {kort(o.tekst)}
                          </option>
                        ))}
                      </select>
                    </Veld>
                  ) : null}

                  {opties &&
                  keuzeState?.activiteit === opties.activiteiten.length - 1 ? (
                    <Veld label="Omschrijving van de activiteit">
                      <input
                        type="text"
                        value={keuzeState?.activiteitOmschrijving ?? ""}
                        onChange={(e) =>
                          setKeuzeState((prev) => ({
                            ...(prev ?? {}),
                            activiteitOmschrijving: e.target.value,
                          }))
                        }
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                  ) : null}

                  <Veld
                    label="Vraag 8d Aantal opdrachtgevers of afnemers"
                    hint="Blijft normaal leeg"
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      value={keuzeState?.aantalAfnemers ?? ""}
                      onChange={(e) =>
                        setKeuzeState((prev) => ({
                          ...(prev ?? {}),
                          aantalAfnemers: e.target.value,
                        }))
                      }
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  {opties ? (
                    <fieldset>
                      <legend className="text-sm font-medium text-gray-900">
                        Vraag 10 Stukken bijvoegen
                      </legend>
                      <div className="mt-1 space-y-1">
                        {opties.stukken.map((o) => (
                          <label
                            key={o.index}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={(keuzeState?.stukken ?? []).includes(
                                o.index,
                              )}
                              onChange={(e) => {
                                const huidig = keuzeState?.stukken ?? [];
                                zetKeuze({
                                  stukken: e.target.checked
                                    ? [...huidig, o.index]
                                    : huidig.filter((i) => i !== o.index),
                                });
                              }}
                              disabled={downloading}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span>{kort(o.tekst, 120)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                </div>

                <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
                  Vraag 11 wordt ondertekend door {input.adviseur.contactpersoon} (
                  {input.adviseur.naam}) met de datum van vandaag,{" "}
                  {toonDatum(input.ondertekendatum)}. Het handtekeningvak blijft
                  leeg om zelf te ondertekenen na het printen.
                </p>
              </details>
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 p-6">
          <p className="text-xs text-gray-500">
            {preview?.bestandsnaam
              ? `Bestandsnaam: ${preview.bestandsnaam}`
              : ""}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={downloading}
              className="rounded-md px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              Annuleer
            </button>
            {/*
              onMouseDown wordt tegengehouden zodat een klik vanuit een
              invoerveld niet verloren gaat: de blur zou anders eerst een
              herberekening starten en de knop onder de cursor vervangen.
            */}
            {geblokkeerd ? (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => download(true)}
                disabled={downloading}
                className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:opacity-50"
                title="Genereert het formulier ondanks de gemelde problemen"
              >
                {downloading ? "Genereren…" : "Toch genereren"}
              </button>
            ) : (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => download(false)}
                disabled={downloading || !input}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
              >
                {downloading ? "Genereren…" : "Genereer PDF"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BelastingdienstFormModal;
